import { Stack } from '@mui/material';
import { useForm } from 'react-typed-form';
import { useParams } from 'react-router';
import PublishingSection from '../../components/PublishingSection';
import { UnsavedChangesBlocker } from '../../components';
import { usePublishing } from '../../hooks';
import { Api, useApiCallable } from '../../api';
import { FieldNumber, FieldTextRequired } from '../../form';

type Props = {
  existing?: Api.ReflectionPoint.V_DetailAsAdmin;
};

export default function ReflectionPointForm({ existing }: Props) {
  const callApi = useApiCallable();
  const { weekId } = useParams();

  const publishing = usePublishing(
    {
      contentType: 'Reflection Point',
      apiPath: 'reflection-points',
      existingId: existing?.id,
    },
    () => form.handleSubmit(),
  );

  const initialValues = {
    title: existing?.title ?? '',
    position: existing?.position ?? 1,
    week: `/program-weeks/${weekId}`,
  };

  const form = useForm<{
    title: string;
    position: number | null;
    week: string;
  }>({
    defaultValues: initialValues,
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      let response;

      if (!existing) {
        setLoading(true);
        response = await callApi(
          `/reflection-points`,
          {
            jsonBody: values,
            method: 'POST',
          },
          {
            addSubmitError,
          },
        );
      } else {
        response = await callApi(
          `/reflection-points/${existing.id}`,
          {
            method: 'PUT',
            jsonBody: values,
          },
          {
            addSubmitError,
          },
        );
      }

      setLoading(false);

      if (!response) return;

      await publishing.handleSubmitSuccess(response, !!existing);
    },
  });

  return (
    <Stack gap={3}>
      <Stack gap={3} maxWidth={600}>
        <FieldTextRequired
          field={form.getField('title')}
          label="Reflection Point"
          helperText="The title will be displayed on the app"
        />
        <FieldNumber
          field={form.getField('position')}
          label="Position"
          helperText="e.g. 1 for the first point that will be shown to the user"
          required
        />
      </Stack>
      <PublishingSection
        existing={existing}
        isLoading={form.isLoading}
        contentType="Reflection Point"
        apiPath="reflection-points"
        publishing={publishing}
      />

      <UnsavedChangesBlocker
        initialValues={initialValues}
        currentValues={form.values}
        isSubmittingRef={publishing.isSubmittingRef}
      />
    </Stack>
  );
}
